// BeforeAfterUpload.js
// Specialized Photo Upload Component for Before-After Album
// Handles category selection (Before/After) and slot positioning

import React, { useState, useRef } from 'react';
import axios from 'axios';

const BeforeAfterUpload = ({ 
    isOpen, 
    onClose, 
    patientContext, 
    onUploadSuccess,
    initialCategory = 'before', // 'before' or 'after'
    initialSlotIndex = 0,        // 0 for bulk upload mode
    isBulkMode = false           // explicit bulk mode flag
}) => {
    // Determine if this is bulk upload (initialSlotIndex = 0 or explicit flag)
    const isBulk = isBulkMode || initialSlotIndex === 0;
    
    // State management
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadCategory, setUploadCategory] = useState(initialCategory);
    const [targetSlotIndex, setTargetSlotIndex] = useState(!isBulk ? initialSlotIndex : 0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [previewImages, setPreviewImages] = useState([]);
    const fileInputRef = useRef(null);

    // File selection handler
    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files);
        
        if (files.length === 0) return;
        
        // Validate file types
        const validFiles = files.filter(file => {
            const isImage = file.type.startsWith('image/');
            const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
            
            if (!isImage) {
                alert(`ไฟล์ ${file.name} ไม่ใช่ไฟล์รูปภาพ`);
                return false;
            }
            
            if (!isValidSize) {
                alert(`ไฟล์ ${file.name} มีขนาดใหญ่เกินไป (สูงสุด 10MB)`);
                return false;
            }
            
            return true;
        });

        if (validFiles.length === 0) return;

        const bakeImageOrientation = (file) => {
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    // Create a hidden canvas
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    
                    // Draw the image (Modern browsers auto-rotate it correctly here!)
                    ctx.drawImage(img, 0, 0);
                    
                    // Convert back to a file without the confusing EXIF tag
                    canvas.toBlob((blob) => {
                        const newFile = new File([blob], file.name, { type: file.type });
                        resolve(newFile);
                    }, file.type, 0.95); // 0.95 keeps high quality
                };
                img.src = URL.createObjectURL(file);
            });
        };

        const bakedFiles = await Promise.all(validFiles.map(file => bakeImageOrientation(file)));

        setSelectedFiles(bakedFiles);
        
        // Create preview images
        const previews = bakedFiles.map(file => {
            const reader = new FileReader();
            return new Promise((resolve) => {
                reader.onload = (e) => resolve({
                    file: file,
                    preview: e.target.result,
                    name: file.name,
                    size: (file.size / 1024 / 1024).toFixed(2) // Size in MB
                });
                reader.readAsDataURL(file);
            });
        });

        Promise.all(previews).then(setPreviewImages);
        
        console.log(`📸 Selected ${bakedFiles.length} files for ${uploadCategory} category`);
    };

    // Handle upload process
    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            alert('กรุณาเลือกไฟล์รูปภาพ');
            return;
        }

        if (!uploadCategory) {
            alert('กรุณาเลือกประเภทรูปภาพ (ก่อน/หลัง)');
            return;
        }

        if (!isBulk && targetSlotIndex === undefined) {
            alert('กรุณาเลือกตำแหน่งช่อง');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            if (isBulk) {
                // BULK UPLOAD: Send each file with auto-assigned slot
                console.log(`📤 Bulk uploading ${selectedFiles.length} ${uploadCategory} photos to consecutive slots`);
                
                let uploadedCount = 0;
                const uploadResults = [];
                
                for (let i = 0; i < selectedFiles.length; i++) {
                    const file = selectedFiles[i];
                    const formData = new FormData();
                    
                    formData.append('photos', file);
                    formData.append('visitDate', patientContext.visitDate || new Date().toISOString().split('T')[0]);
                    formData.append('doctorName', patientContext.doctor || 'Staff');
                    formData.append('albumType', uploadCategory);
                    formData.append('slotIndex', i.toString()); // Auto-assign slots 0, 1, 2, ...
                    formData.append('albumCategory', 'before-after');
                    formData.append('notes', `Bulk Upload - ${uploadCategory.toUpperCase()} - Photo ${i + 1}/${selectedFiles.length}`);
                    
                    const response = await axios.post(
                        `/api/patient/${patientContext.branchHN}/upload-before-after-photos`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                            onUploadProgress: (progressEvent) => {
                                const itemProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                const totalProgress = Math.round(((i * 100 + itemProgress) / selectedFiles.length));
                                setUploadProgress(totalProgress);
                            }
                        }
                    );
                    
                    if (response.data.success) {
                        uploadedCount++;
                        const returnedPhotos = Array.isArray(response.data.photos) ? response.data.photos : [];
                        // Attach file preview to each returned photo
                        returnedPhotos.forEach((photo, idx) => {
                            if (photo && previewImages[i + idx]?.preview) {
                                photo.url = previewImages[i + idx].preview;
                                photo.preview = previewImages[i + idx].preview;
                            }
                        });
                        uploadResults.push(returnedPhotos);
                        console.log(`✅ Uploaded ${i + 1}/${selectedFiles.length}: ${file.name}`);
                    } else {
                        console.warn(`⚠️ Upload failed for ${file.name}: ${response.data.error}`);
                    }
                }
                
                console.log(`✅ Bulk upload complete: ${uploadedCount}/${selectedFiles.length} files uploaded`);
                
                if (onUploadSuccess) {
                    onUploadSuccess({
                        category: uploadCategory,
                        bulkUpload: true,
                        totalFiles: selectedFiles.length,
                        uploadedFiles: uploadedCount,
                        photos: uploadResults
                    });
                }
                
                onClose();
                
            } else {
                // SINGLE SLOT UPLOAD: All files go to one slot
                const formData = new FormData();
                
                selectedFiles.forEach((file, index) => {
                    formData.append('photos', file);
                });
                
                formData.append('visitDate', patientContext.visitDate || new Date().toISOString().split('T')[0]);
                formData.append('doctorName', patientContext.doctor || 'Staff');
                formData.append('notes', `Before-After Album - ${uploadCategory.toUpperCase()}`);
                formData.append('albumType', uploadCategory);
                formData.append('slotIndex', targetSlotIndex.toString());
                formData.append('albumCategory', 'before-after');
                
                console.log(`📤 Uploading ${selectedFiles.length} ${uploadCategory} photos to slot ${targetSlotIndex}`);

                const response = await axios.post(
                    `/api/patient/${patientContext.branchHN}/upload-before-after-photos`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        onUploadProgress: (progressEvent) => {
                            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            setUploadProgress(progress);
                        }
                    }
                );

                if (response.data.success) {
                    console.log('✅ Before-After upload successful:', response.data);
                    const returnedPhotos = Array.isArray(response.data.photos) ? response.data.photos : [];
                    // Attach file previews to each returned photo
                    returnedPhotos.forEach((photo, idx) => {
                        if (photo && previewImages[idx]?.preview) {
                            photo.url = previewImages[idx].preview;
                            photo.preview = previewImages[idx].preview;
                        }
                    });
                    
                    if (onUploadSuccess) {
                        onUploadSuccess({
                            category: uploadCategory,
                            slotIndex: targetSlotIndex,
                            photos: returnedPhotos,
                            visitCount: response.data.visitCount
                        });
                    }
                   
                    onClose();
                    
                } else {
                    throw new Error(response.data.error || 'Upload failed');
                }
            }
            
        } catch (error) {
            console.error('❌ Upload error:', error);
            
            const errorMessage = error.response?.data?.error || error.message;
            alert(`เกิดข้อผิดพลาดในการอัปโหลด:\n${errorMessage}`);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    // Reset form
    const resetForm = () => {
        setSelectedFiles([]);
        setPreviewImages([]);
        setUploadProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle modal close
    const handleClose = () => {
        if (isUploading) {
            const confirmClose = window.confirm('กำลังอัปโหลดอยู่ ต้องการยกเลิกหรือไม่?');
            if (!confirmClose) return;
        }
        
        resetForm();
        onClose();
    };

    // Don't render if not open
    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '25px',
                    borderBottom: '2px solid #f0f0f0',
                    paddingBottom: '15px'
                }}>
                    <h2 style={{
                        margin: 0,
                        color: '#495057',
                        fontSize: '22px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        📸 {isBulk ? '📤 Bulk Upload' : 'อัปโหลดรูป Before-After'}
                        {isBulk && <span style={{ fontSize: '14px', backgroundColor: '#4CAF50', color: 'white', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>BULK MODE</span>}
                    </h2>
                    
                    <button
                        onClick={handleClose}
                        disabled={isUploading}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: isUploading ? 'not-allowed' : 'pointer',
                            opacity: isUploading ? 0.5 : 1
                        }}
                    >
                        ✖️
                    </button>
                </div>

                {/* Patient Info */}
                <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '25px',
                    border: '1px solid #dee2e6'
                }}>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                        <strong>👤 ผู้ป่วย:</strong> {patientContext.name} - {patientContext.branchHN}<br />
                        <strong>📅 วันที่:</strong> {patientContext.visitDate}<br />
                        <strong>👨‍⚕️ แพทย์:</strong> {patientContext.doctor || 'Staff'}
                    </div>
                </div>

                {/* Category & Slot Selection */}
                <div style={{
                    display: isBulk ? 'block' : 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '20px',
                    marginBottom: '25px'
                }}>
                    {/* Category Selection */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontWeight: 'bold',
                            color: '#495057'
                        }}>
                            ประเภทรูปภาพ / Photo Type:
                        </label>
                        
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 15px',
                                borderRadius: '6px',
                                backgroundColor: uploadCategory === 'before' ? '#d4edda' : '#f8f9fa',
                                border: `2px solid ${uploadCategory === 'before' ? '#28a745' : '#dee2e6'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                flex: 1
                            }}>
                                <input
                                    type="radio"
                                    name="category"
                                    value="before"
                                    checked={uploadCategory === 'before'}
                                    onChange={(e) => setUploadCategory(e.target.value)}
                                    disabled={isUploading}
                                />
                                <span style={{ color: uploadCategory === 'before' ? '#155724' : '#6c757d' }}>
                                    ❄️ ก่อน (Before)
                                </span>
                            </label>
                            
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 15px',
                                borderRadius: '6px',
                                backgroundColor: uploadCategory === 'after' ? '#cce5ff' : '#f8f9fa',
                                border: `2px solid ${uploadCategory === 'after' ? '#007bff' : '#dee2e6'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                flex: 1
                            }}>
                                <input
                                    type="radio"
                                    name="category"
                                    value="after"
                                    checked={uploadCategory === 'after'}
                                    onChange={(e) => setUploadCategory(e.target.value)}
                                    disabled={isUploading}
                                />
                                <span style={{ color: uploadCategory === 'after' ? '#004085' : '#6c757d' }}>
                                    🌸 หลัง (After)
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Slot Position Selection - HIDDEN IN BULK MODE */}
                    {!isBulk && (
                        <div>
                            <label style={{
                                display: 'block',
                                marginBottom: '8px',
                                fontWeight: 'bold',
                                color: '#495057'
                            }}>
                                ตำแหน่งในกริด:
                            </label>
                            
                            <select
                                value={targetSlotIndex}
                                onChange={(e) => setTargetSlotIndex(parseInt(e.target.value))}
                                disabled={isUploading}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ced4da',
                                    borderRadius: '6px',
                                    fontSize: '14px'
                                }}
                            >
                                <option value={0}>ช่องที่ 1 (L ซ้าย)</option>
                                <option value={1}>ช่องที่ 2 (C กลาง)</option>
                                <option value={2}>ช่องที่ 3 (R ขวา)</option>
                                <option value={3}>ช่องที่ 4 (L ซ้าย)</option>
                                <option value={4}>ช่องที่ 5 (C กลาง)</option>
                                <option value={5}>ช่องที่ 6 (R ขวา)</option>
                                <option value={6}>ช่องที่ 7 (L ซ้าย)</option>
                                <option value={7}>ช่องที่ 8 (C กลาง)</option>
                                <option value={8}>ช่องที่ 9 (R ขวา)</option>
                                <option value={9}>ช่องที่ 10 (L ซ้าย)</option>
                                <option value={10}>ช่องที่ 11 (C กลาง)</option>
                                <option value={11}>ช่องที่ 12 (R ขวา)</option>
                            </select>
                        </div>
                    )}

                    {/* Bulk Mode Info */}
                    {isBulk && (
                        <div style={{
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #4CAF50',
                            borderRadius: '6px',
                            padding: '12px',
                            color: '#2e7d32',
                            fontSize: '14px'
                        }}>
                            <strong>💡 Bulk Upload Mode:</strong><br/>
                            Just select all your images at once. Photos will auto-fill slots 1, 2, 3, etc. (Left → Center → Right)
                        </div>
                    )}
                </div>

                {/* File Selection */}
                <div style={{ marginBottom: '25px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '10px',
                        fontWeight: 'bold',
                        color: '#495057'
                    }}>
                        {isBulk ? '📤 Select All Images / เลือกรูปทั้งหมด:' : 'เลือกไฟล์รูปภาพ:'}
                    </label>
                    
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        disabled={isUploading}
                        style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px dashed #dee2e6',
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                        }}
                    />
                    
                    <div style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        marginTop: '8px'
                    }}>
                        💡 {isBulk 
                            ? '✅ Select 3-20+ images. They will auto-fill all available slots (recommended for quick bulk upload like LINE)' 
                            : 'รองรับ: JPG, PNG, GIF (สูงสุด 10MB ต่อไฟล์) | เลือกได้หลายไฟล์พร้อมกัน'}
                    </div>
                </div>

                {/* File Previews */}
                {previewImages.length > 0 && (
                    <div style={{ marginBottom: '25px' }}>
                        <h4 style={{ color: '#495057', marginBottom: '15px' }}>
                            📋 ไฟล์ที่เลือก ({previewImages.length} ไฟล์):
                        </h4>
                        
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                            gap: '15px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            padding: '10px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px'
                        }}>
                            {previewImages.map((item, index) => (
                                <div
                                    key={index}
                                    style={{
                                        textAlign: 'center',
                                        padding: '8px',
                                        backgroundColor: 'white',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6'
                                    }}
                                >
                                    <img
                                        src={item.preview}
                                        alt={item.name}
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                            marginBottom: '5px'
                                        }}
                                    />
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#6c757d',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {item.name}
                                    </div>
                                    <div style={{
                                        fontSize: '9px',
                                        color: '#28a745',
                                        marginTop: '2px'
                                    }}>
                                        {item.size} MB
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Upload Progress */}
                {isUploading && (
                    <div style={{
                        marginBottom: '25px',
                        padding: '15px',
                        backgroundColor: '#cce5ff',
                        borderRadius: '8px',
                        border: '1px solid #b3d9ff'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '10px'
                        }}>
                            <span style={{ color: '#004085', fontWeight: 'bold' }}>
                                🔄 กำลังอัปโหลด...
                            </span>
                            <span style={{ color: '#004085' }}>
                                {uploadProgress}%
                            </span>
                        </div>
                        
                        <div style={{
                            width: '100%',
                            backgroundColor: '#b3d9ff',
                            borderRadius: '10px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                width: `${uploadProgress}%`,
                                height: '8px',
                                backgroundColor: '#007bff',
                                transition: 'width 0.3s ease'
                            }}></div>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '15px',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={handleClose}
                        disabled={isUploading}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isUploading ? 'not-allowed' : 'pointer',
                            opacity: isUploading ? 0.6 : 1
                        }}
                    >
                        ยกเลิก
                    </button>
                    
                    <button
                        onClick={resetForm}
                        disabled={isUploading || selectedFiles.length === 0}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            backgroundColor: '#ffc107',
                            color: '#212529',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: (isUploading || selectedFiles.length === 0) ? 'not-allowed' : 'pointer',
                            opacity: (isUploading || selectedFiles.length === 0) ? 0.6 : 1
                        }}
                    >
                        🔄 เริ่มใหม่
                    </button>
                    
                    <button
                        onClick={handleUpload}
                        disabled={isUploading || selectedFiles.length === 0}
                        style={{
                            padding: '12px 24px',
                            fontSize: '16px',
                            backgroundColor: selectedFiles.length > 0 ? (isBulk ? '#FF9800' : '#28a745') : '#cccccc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: (isUploading || selectedFiles.length === 0) ? 'not-allowed' : 'pointer',
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isUploading 
                            ? '🔄 กำลังอัปโหลด...' 
                            : isBulk
                            ? `📤 BULK UPLOAD (${selectedFiles.length} รูป)`
                            : `📤 อัปโหลด (${selectedFiles.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BeforeAfterUpload;